import { useEffect, useState, type ReactNode } from 'react';
import { FluentProvider, Toaster } from '@fluentui/react-components';
import { opusNovaDarkTheme, opusNovaLightTheme } from '@supernova/design-system';
import { useThemeStore, resolveTheme, type ResolvedTheme } from '../stores/themeStore.js';
import { ConfirmProvider } from './ConfirmDialog.js';

/** Toaster global compartido por toda la app — usar con useToastController(GLOBAL_TOASTER_ID). */
export const GLOBAL_TOASTER_ID = 'supernova-toaster';

/**
 * Aplica el tema activo a la app: sincroniza Fluent v9 (`FluentProvider`) con los
 * CSS vars del design system (`<html data-theme>`). Reacciona a:
 *   - cambios del store de tema (usuario eligió otro modo)
 *   - cambios del prefers-color-scheme del sistema (modo "system")
 */
export function ThemedProvider({ children }: { children: ReactNode }) {
  const mode = useThemeStore((s) => s.mode);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(mode));

  useEffect(() => {
    setResolved(resolveTheme(mode));
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => setResolved(resolveTheme('system'));
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolved);
  }, [resolved]);

  return (
    <FluentProvider
      theme={resolved === 'light' ? opusNovaLightTheme : opusNovaDarkTheme}
      style={{
        backgroundColor: 'var(--bg-page)',
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <ConfirmProvider>{children}</ConfirmProvider>
      <Toaster toasterId={GLOBAL_TOASTER_ID} position="bottom" />
    </FluentProvider>
  );
}
